import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Type,
  Square,
  ArrowRight,
  Circle,
  Minus,
  ImagePlus,
  Smile,
  Mic,
  Copy,
  Trash2,
  MousePointerClick,
  EyeOff,
} from 'lucide-react';
import { BLUR_STRENGTH } from '../lib/videoBlur';
import { PanelHeader, PanelSectionTitle, TimingFields } from './EditorPanelParts';
import { formatPanelTime } from '../lib/editorTime';
import { VIDEO_EDITOR_FONT_FAMILIES, loadGoogleFontIfNeeded } from '../constants/videoEditorFonts';
import type { VideoTextAlign } from '../utils/videoEditorTextCanvas';

/** Minimal text-annotation shape (parent owns full Annotation type). */
export interface VideoEditorTextAnn {
  id: string;
  type: 'text';
  text: string;
  color: string;
  fontSize: number;
  fontFamily?: string;
  backgroundColor?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  textAlign?: VideoTextAlign;
  textBoxWidthRatio?: number;
}

export type VideoEditorPropertiesSelection =
  | (VideoEditorTextAnn & { x: number; y: number })
  | {
      id: string;
      type: 'rect';
      x: number;
      y: number;
      w: number;
      h: number;
      color: string;
      fillColor?: string;
    }
  | {
      id: string;
      type: 'circle';
      x: number;
      y: number;
      w: number;
      h: number;
      color: string;
      fillColor?: string;
    }
  | {
      id: string;
      type: 'blur';
      x: number;
      y: number;
      w: number;
      h: number;
      blurStyle: 'pixelate' | 'blur';
      blurStrength: number;
    }
  | {
      id: string;
      type: 'arrow';
      x: number;
      y: number;
      w: number;
      h: number;
      color: string;
    }
  | {
      id: string;
      type: 'line';
      x: number;
      y: number;
      w: number;
      h: number;
      color: string;
    }
  | {
      id: string;
      type: 'image';
      x: number;
      y: number;
      w: number;
      h: number;
      imageSrc: string;
      color: string;
    }
  | {
      id: string;
      type: 'emoji';
      x: number;
      y: number;
      text: string;
      color: string;
      fontSize: number;
    }
  | {
      id: string;
      type: 'audio';
      x: number;
      y: number;
      w: number;
      h: number;
      text: string;
      color: string;
      fontSize: number;
      audioSrc: string;
      audioDurationSec?: number;
      audioVolume?: number;
    };

type AnnPatch = Record<string, string | number | boolean | undefined>;

interface VideoEditorPropertiesPanelProps {
  selected: VideoEditorPropertiesSelection | null;
  onApplyPatch: (id: string, patch: AnnPatch) => void;
  onCommitHistory: () => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  /** Shown above the hint when no annotation is selected (e.g. the Frame settings). */
  emptyContent?: React.ReactNode;
  /** When the selected item is shown (seconds) and the video length, for the Timing section. */
  timing?: { inTime: number; outTime: number; duration: number };
}

export function VideoEditorPropertiesPanel({
  selected,
  onApplyPatch,
  onCommitHistory,
  onDelete,
  onDuplicate,
  emptyContent,
  timing,
}: VideoEditorPropertiesPanelProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (selected?.type === 'text' && selected.fontFamily) {
      loadGoogleFontIfNeeded(selected.fontFamily);
    }
  }, [selected]);

  const patch = (p: AnnPatch) => {
    if (!selected) return;
    onApplyPatch(selected.id, p);
  };

  const commitPatch = (p: AnnPatch) => {
    if (!selected) return;
    onApplyPatch(selected.id, p);
    onCommitHistory();
  };

  const ratioToPct = (r: number) => Math.round(r * 1000) / 10;
  const pctToRatio = (pct: number) => Math.max(0, Math.min(100, pct)) / 100;
  const pctToRatioSigned = (pct: number) => Math.max(-100, Math.min(100, pct)) / 100;

  if (!selected) {
    return (
      <aside className="ve-props-panel" aria-label={t('video_editor_props.panel_aria')}>
        {emptyContent}
        <div className="ve-props-empty-card">
          <MousePointerClick size={16} aria-hidden />
          <div>
            <strong>{t('video_editor_panel.empty_title', 'Edit an item')}</strong>
            <p className="ve-props-empty">{t('video_editor_props.empty')}</p>
            <p className="ve-props-empty">{t('video_editor_panel.zoom_tip', 'Tip: press Z to zoom in at the playhead.')}</p>
          </div>
        </div>
      </aside>
    );
  }

  const timingSubtitle = timing
    ? t('video_editor_panel.shown', 'Shown {{start}} – {{end}}', {
        start: formatPanelTime(timing.inTime),
        end: formatPanelTime(timing.outTime),
      })
    : undefined;

  const actionBar = (
    <>
    {timing && (
      <>
        <PanelSectionTitle>{t('video_editor_panel.timing', 'Timing')}</PanelSectionTitle>
        <TimingFields
          start={timing.inTime}
          end={timing.outTime}
          duration={timing.duration}
          onChange={(inTime, outTime) => patch({ inTime, outTime })}
          onCommit={onCommitHistory}
        />
      </>
    )}
    <div className="ve-props-actions">
      <button
        type="button"
        className="ve-props-btn-duplicate"
        onClick={() => onDuplicate(selected.id)}
        aria-label={t('video_editor_props.duplicate')}
      >
        <Copy size={14} aria-hidden />
        {t('video_editor_props.duplicate')}
      </button>
      <button
        type="button"
        className="ve-props-btn-delete"
        onClick={() => onDelete(selected.id)}
        aria-label={t('video_editor_props.delete')}
      >
        <Trash2 size={14} aria-hidden />
        {t('video_editor_props.delete')}
      </button>
    </div>
    </>
  );

  if (selected.type === 'rect') {
    return (
      <aside className="ve-props-panel" aria-label={t('video_editor_props.shape_props_aria')}>
        <PanelHeader icon={<Square size={16} />} title={t('video_editor_props.rect_title')} subtitle={timingSubtitle} />

        <PanelSectionTitle>{t('video_editor_props.canvas_section')}</PanelSectionTitle>
        <div className="ve-props-canvas-grid">
          <label className="ve-props-mini-label" htmlFor="ve-r-x">
            {t('video_editor_props.canvas_x_pct')}
            <input
              id="ve-r-x"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.x)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ x: pctToRatio(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-r-y">
            {t('video_editor_props.canvas_y_pct')}
            <input
              id="ve-r-y"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.y)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ y: pctToRatio(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-r-w">
            {t('video_editor_props.canvas_w_pct')}
            <input
              id="ve-r-w"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.w)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ w: pctToRatioSigned(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-r-h">
            {t('video_editor_props.canvas_h_pct')}
            <input
              id="ve-r-h"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.h)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ h: pctToRatioSigned(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
        </div>
        <p className="ve-props-hint">{t('video_editor_props.canvas_hint_rect')}</p>

        <PanelSectionTitle>{t('video_editor_panel.appearance', 'Appearance')}</PanelSectionTitle>

        <div className="ve-props-row ve-props-colors">
          <label className="ve-props-label">
            {t('video_editor_props.stroke')}
            <input
              type="color"
              className="ve-props-color"
              value={toHexColor(selected.color)}
              onChange={e => patch({ color: e.target.value })}
              onBlur={() => onCommitHistory()}
              aria-label={t('video_editor_props.stroke')}
            />
          </label>
          <label className="ve-props-label">
            {t('video_editor_props.fill')}
            <input
              type="color"
              className="ve-props-color"
              value={toHexColor(selected.fillColor === 'transparent' || !selected.fillColor ? '#000000' : selected.fillColor)}
              onChange={e => patch({ fillColor: e.target.value })}
              onBlur={() => onCommitHistory()}
              aria-label={t('video_editor_props.fill')}
            />
          </label>
          <button
            type="button"
            className="ve-props-btn-secondary"
            onClick={() => commitPatch({ fillColor: 'transparent' })}
          >
            {t('video_editor_props.clear_fill')}
          </button>
        </div>

        {actionBar}
      </aside>
    );
  }

  if (selected.type === 'blur') {
    const box: { key: 'x' | 'y' | 'w' | 'h'; label: string }[] = [
      { key: 'x', label: t('video_editor_props.canvas_x_pct') },
      { key: 'y', label: t('video_editor_props.canvas_y_pct') },
      { key: 'w', label: t('video_editor_props.canvas_w_pct') },
      { key: 'h', label: t('video_editor_props.canvas_h_pct') },
    ];
    return (
      <aside className="ve-props-panel" aria-label={t('video_editor_props.shape_props_aria')}>
        <PanelHeader icon={<EyeOff size={16} />} title={t('video_editor_blur.label', 'Blur')} subtitle={timingSubtitle} />
        <p className="ve-props-hint">{t('video_editor_blur.hint', 'Hides this area of the video. It stays on the same spot while zoomed.')}</p>

        <PanelSectionTitle>{t('video_editor_props.canvas_section')}</PanelSectionTitle>
        <div className="ve-props-canvas-grid">
          {box.map(({ key, label }) => (
            <label key={key} className="ve-props-mini-label" htmlFor={`ve-b-${key}`}>
              {label}
              <input
                id={`ve-b-${key}`}
                type="number"
                className="ve-props-num"
                step={0.1}
                value={ratioToPct(selected[key])}
                onChange={e => {
                  const v = Number(e.target.value);
                  if (!Number.isFinite(v)) return;
                  patch({ [key]: key === 'w' || key === 'h' ? pctToRatioSigned(v) : pctToRatio(v) });
                }}
                onBlur={() => onCommitHistory()}
              />
            </label>
          ))}
        </div>

        <PanelSectionTitle>{t('video_editor_panel.appearance', 'Appearance')}</PanelSectionTitle>
        <div className="ve-props-label">
          {t('video_editor_blur.style', 'Style')}
          <div className="ve-props-toggle-row" role="group" aria-label={t('video_editor_blur.style', 'Style')}>
            {(['pixelate', 'blur'] as const).map(style => (
              <button
                key={style}
                type="button"
                className={`ve-props-toggle${selected.blurStyle === style ? ' active' : ''}`}
                onClick={() => commitPatch({ blurStyle: style })}
                aria-pressed={selected.blurStyle === style}
              >
                {style === 'pixelate' ? t('video_editor_blur.pixelate', 'Pixelate') : t('video_editor_blur.soft', 'Blur')}
              </button>
            ))}
          </div>
        </div>
        <div className="ve-props-row">
          <label className="ve-props-label ve-props-grow" htmlFor="ve-b-strength">
            {t('video_editor_blur.strength', 'Strength')}
            <input
              id="ve-b-strength"
              type="range"
              min={BLUR_STRENGTH.min}
              max={BLUR_STRENGTH.max}
              value={selected.blurStrength}
              onChange={e => patch({ blurStrength: Number(e.target.value) })}
              onMouseUp={onCommitHistory}
              onKeyUp={onCommitHistory}
            />
          </label>
          <span className="ve-props-size-value">{selected.blurStrength}px</span>
        </div>

        {actionBar}
      </aside>
    );
  }

  if (selected.type === 'arrow') {
    return (
      <aside className="ve-props-panel" aria-label={t('video_editor_props.shape_props_aria')}>
        <PanelHeader icon={<ArrowRight size={16} />} title={t('video_editor_props.arrow_title')} subtitle={timingSubtitle} />

        <PanelSectionTitle>{t('video_editor_props.canvas_section')}</PanelSectionTitle>
        <div className="ve-props-canvas-grid">
          <label className="ve-props-mini-label" htmlFor="ve-a-x">
            {t('video_editor_props.canvas_x_pct')}
            <input
              id="ve-a-x"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.x)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ x: pctToRatio(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-a-y">
            {t('video_editor_props.canvas_y_pct')}
            <input
              id="ve-a-y"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.y)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ y: pctToRatio(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-a-w">
            {t('video_editor_props.canvas_dx_pct')}
            <input
              id="ve-a-w"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.w)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ w: pctToRatioSigned(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-a-h">
            {t('video_editor_props.canvas_dy_pct')}
            <input
              id="ve-a-h"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.h)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ h: pctToRatioSigned(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
        </div>
        <p className="ve-props-hint">{t('video_editor_props.canvas_hint_arrow')}</p>

        <PanelSectionTitle>{t('video_editor_panel.appearance', 'Appearance')}</PanelSectionTitle>

        <label className="ve-props-label">
          {t('video_editor_props.stroke')}
          <input
            type="color"
            className="ve-props-color"
            value={toHexColor(selected.color)}
            onChange={e => patch({ color: e.target.value })}
            onBlur={() => onCommitHistory()}
            aria-label={t('video_editor_props.stroke')}
          />
        </label>

        {actionBar}
      </aside>
    );
  }

  if (selected.type === 'circle') {
    return (
      <aside className="ve-props-panel" aria-label={t('video_editor_props.shape_props_aria')}>
        <PanelHeader icon={<Circle size={16} />} title={t('video_editor_props.circle_title')} subtitle={timingSubtitle} />

        <PanelSectionTitle>{t('video_editor_props.canvas_section')}</PanelSectionTitle>
        <div className="ve-props-canvas-grid">
          <label className="ve-props-mini-label" htmlFor="ve-c-x">
            {t('video_editor_props.canvas_x_pct')}
            <input
              id="ve-c-x"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.x)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ x: pctToRatio(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-c-y">
            {t('video_editor_props.canvas_y_pct')}
            <input
              id="ve-c-y"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.y)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ y: pctToRatio(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-c-w">
            {t('video_editor_props.canvas_w_pct')}
            <input
              id="ve-c-w"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.w)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ w: pctToRatioSigned(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-c-h">
            {t('video_editor_props.canvas_h_pct')}
            <input
              id="ve-c-h"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.h)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ h: pctToRatioSigned(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
        </div>
        <p className="ve-props-hint">{t('video_editor_props.canvas_hint_circle')}</p>

        <PanelSectionTitle>{t('video_editor_panel.appearance', 'Appearance')}</PanelSectionTitle>

        <div className="ve-props-row ve-props-colors">
          <label className="ve-props-label">
            {t('video_editor_props.stroke')}
            <input
              type="color"
              className="ve-props-color"
              value={toHexColor(selected.color)}
              onChange={e => patch({ color: e.target.value })}
              onBlur={() => onCommitHistory()}
              aria-label={t('video_editor_props.stroke')}
            />
          </label>
          <label className="ve-props-label">
            {t('video_editor_props.fill')}
            <input
              type="color"
              className="ve-props-color"
              value={toHexColor(selected.fillColor === 'transparent' || !selected.fillColor ? '#000000' : selected.fillColor)}
              onChange={e => patch({ fillColor: e.target.value })}
              onBlur={() => onCommitHistory()}
              aria-label={t('video_editor_props.fill')}
            />
          </label>
          <button
            type="button"
            className="ve-props-btn-secondary"
            onClick={() => commitPatch({ fillColor: 'transparent' })}
          >
            {t('video_editor_props.clear_fill')}
          </button>
        </div>

        {actionBar}
      </aside>
    );
  }

  if (selected.type === 'line') {
    return (
      <aside className="ve-props-panel" aria-label={t('video_editor_props.shape_props_aria')}>
        <PanelHeader icon={<Minus size={16} />} title={t('video_editor_props.line_title')} subtitle={timingSubtitle} />

        <PanelSectionTitle>{t('video_editor_props.canvas_section')}</PanelSectionTitle>
        <div className="ve-props-canvas-grid">
          <label className="ve-props-mini-label" htmlFor="ve-l-x">
            {t('video_editor_props.canvas_x_pct')}
            <input
              id="ve-l-x"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.x)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ x: pctToRatio(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-l-y">
            {t('video_editor_props.canvas_y_pct')}
            <input
              id="ve-l-y"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.y)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ y: pctToRatio(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-l-w">
            {t('video_editor_props.canvas_dx_pct')}
            <input
              id="ve-l-w"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.w)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ w: pctToRatioSigned(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-l-h">
            {t('video_editor_props.canvas_dy_pct')}
            <input
              id="ve-l-h"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.h)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ h: pctToRatioSigned(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
        </div>
        <p className="ve-props-hint">{t('video_editor_props.canvas_hint_line')}</p>

        <PanelSectionTitle>{t('video_editor_panel.appearance', 'Appearance')}</PanelSectionTitle>

        <label className="ve-props-label">
          {t('video_editor_props.stroke')}
          <input
            type="color"
            className="ve-props-color"
            value={toHexColor(selected.color)}
            onChange={e => patch({ color: e.target.value })}
            onBlur={() => onCommitHistory()}
            aria-label={t('video_editor_props.stroke')}
          />
        </label>

        {actionBar}
      </aside>
    );
  }

  if (selected.type === 'image') {
    return (
      <aside className="ve-props-panel" aria-label={t('video_editor_props.shape_props_aria')}>
        <PanelHeader icon={<ImagePlus size={16} />} title={t('video_editor_props.image_title')} subtitle={timingSubtitle} />

        <PanelSectionTitle>{t('video_editor_props.canvas_section')}</PanelSectionTitle>
        <div className="ve-props-canvas-grid">
          <label className="ve-props-mini-label" htmlFor="ve-i-x">
            {t('video_editor_props.canvas_x_pct')}
            <input
              id="ve-i-x"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.x)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ x: pctToRatio(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-i-y">
            {t('video_editor_props.canvas_y_pct')}
            <input
              id="ve-i-y"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.y)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ y: pctToRatio(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-i-w">
            {t('video_editor_props.canvas_w_pct')}
            <input
              id="ve-i-w"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.w)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ w: pctToRatioSigned(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-i-h">
            {t('video_editor_props.canvas_h_pct')}
            <input
              id="ve-i-h"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.h)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ h: pctToRatioSigned(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
        </div>
        <p className="ve-props-hint">{t('video_editor_props.canvas_hint_image')}</p>

        <PanelSectionTitle>{t('video_editor_panel.appearance', 'Appearance')}</PanelSectionTitle>

        <label className="ve-props-label">
          {t('video_editor_props.replace_image')}
          <input
            type="file"
            accept="image/*"
            className="ve-props-file"
            onChange={e => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f || !f.type.startsWith('image/')) return;
              const fr = new FileReader();
              fr.onload = () => commitPatch({ imageSrc: fr.result as string });
              fr.readAsDataURL(f);
            }}
          />
        </label>

        {actionBar}
      </aside>
    );
  }

  if (selected.type === 'emoji') {
    return (
      <aside className="ve-props-panel" aria-label={t('video_editor_props.text_props_aria')}>
        <PanelHeader icon={<Smile size={16} />} title={t('video_editor_props.emoji_title')} subtitle={timingSubtitle} />

        <PanelSectionTitle>{t('video_editor_props.canvas_section')}</PanelSectionTitle>
        <div className="ve-props-canvas-grid ve-props-canvas-grid--2">
          <label className="ve-props-mini-label" htmlFor="ve-e-x">
            {t('video_editor_props.canvas_x_pct')}
            <input
              id="ve-e-x"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.x)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ x: pctToRatio(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-e-y">
            {t('video_editor_props.canvas_y_pct')}
            <input
              id="ve-e-y"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.y)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ y: pctToRatio(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
        </div>

        <PanelSectionTitle>{t('video_editor_panel.appearance', 'Appearance')}</PanelSectionTitle>

        <label className="ve-props-label" htmlFor="ve-e-char">
          {t('video_editor_props.emoji_char')}
          <input
            id="ve-e-char"
            type="text"
            className="ve-props-textarea ve-props-emoji-input"
            maxLength={8}
            value={selected.text}
            onChange={e => patch({ text: e.target.value || '🙂' })}
            onBlur={() => onCommitHistory()}
          />
        </label>

        <div className="ve-props-row">
          <label className="ve-props-label ve-props-grow" htmlFor="ve-e-size">
            {t('video_editor_props.size')}
            <input
              id="ve-e-size"
              type="range"
              min={16}
              max={160}
              value={selected.fontSize}
              onChange={e => patch({ fontSize: Number(e.target.value) })}
              onMouseUp={onCommitHistory}
            />
          </label>
          <span className="ve-props-size-value">{selected.fontSize}px</span>
        </div>

        <label className="ve-props-label">
          {t('video_editor_props.emoji_color')}
          <input
            type="color"
            className="ve-props-color"
            value={toHexColor(selected.color)}
            onChange={e => patch({ color: e.target.value })}
            onBlur={() => onCommitHistory()}
            aria-label={t('video_editor_props.emoji_color')}
          />
        </label>

        {actionBar}
      </aside>
    );
  }

  if (selected.type === 'audio') {
    const durLabel =
      selected.audioDurationSec != null && isFinite(selected.audioDurationSec)
        ? `${selected.audioDurationSec.toFixed(1)}s`
        : '—';
    return (
      <aside className="ve-props-panel" aria-label={t('video_editor_props.audio_props_aria')}>
        <PanelHeader icon={<Mic size={16} />} title={t('video_editor_props.audio_title')} subtitle={timingSubtitle} />
        <p className="ve-props-hint">{t('video_editor_props.audio_hint')}</p>

        <PanelSectionTitle>{t('video_editor_props.canvas_section')}</PanelSectionTitle>
        <div className="ve-props-canvas-grid">
          <label className="ve-props-mini-label" htmlFor="ve-au-x">
            {t('video_editor_props.canvas_x_pct')}
            <input
              id="ve-au-x"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.x)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ x: pctToRatio(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-au-y">
            {t('video_editor_props.canvas_y_pct')}
            <input
              id="ve-au-y"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.y)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ y: pctToRatio(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-au-w">
            {t('video_editor_props.canvas_w_pct')}
            <input
              id="ve-au-w"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.w)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ w: pctToRatioSigned(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
          <label className="ve-props-mini-label" htmlFor="ve-au-h">
            {t('video_editor_props.canvas_h_pct')}
            <input
              id="ve-au-h"
              type="number"
              className="ve-props-num"
              step={0.1}
              value={ratioToPct(selected.h)}
              onChange={e => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                patch({ h: pctToRatioSigned(v) });
              }}
              onBlur={() => onCommitHistory()}
            />
          </label>
        </div>

        <PanelSectionTitle>{t('video_editor_panel.appearance', 'Appearance')}</PanelSectionTitle>

        <label className="ve-props-label" htmlFor="ve-au-label">
          {t('video_editor_props.audio_label_field')}
          <input
            id="ve-au-label"
            type="text"
            className="ve-props-select"
            value={selected.text}
            onChange={e => patch({ text: e.target.value })}
            onBlur={() => onCommitHistory()}
          />
        </label>

        <div className="ve-props-row">
          <label className="ve-props-label ve-props-grow" htmlFor="ve-au-vol">
            {t('video_editor_props.audio_volume')}
            <input
              id="ve-au-vol"
              type="range"
              min={0}
              max={100}
              step={1}
              value={Math.round((selected.audioVolume ?? 1) * 100)}
              onChange={e => patch({ audioVolume: Number(e.target.value) / 100 })}
              onMouseUp={onCommitHistory}
              onKeyUp={e => e.key === 'Enter' && onCommitHistory()}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round((selected.audioVolume ?? 1) * 100)}
              aria-label={t('video_editor_props.audio_volume')}
            />
          </label>
          <span className="ve-props-size-value">{Math.round((selected.audioVolume ?? 1) * 100)}%</span>
        </div>
        <p className="ve-props-mini-meta">{t('video_editor_props.audio_volume_hint')}</p>

        <p className="ve-props-mini-meta">
          {t('video_editor_props.audio_duration_meta', { duration: durLabel })}
        </p>

        <label className="ve-props-label">
          {t('video_editor_props.audio_badge_color')}
          <input
            type="color"
            className="ve-props-color"
            value={toHexColor(selected.color)}
            onChange={e => patch({ color: e.target.value })}
            onBlur={() => onCommitHistory()}
            aria-label={t('video_editor_props.audio_badge_color')}
          />
        </label>

        {actionBar}
      </aside>
    );
  }

  /* text */
  const align = selected.textAlign ?? 'left';
  const alignBtn = (a: VideoTextAlign, Icon: React.ComponentType<{ size?: number }>, label: string) => (
    <button
      type="button"
      className={`ve-props-toggle${align === a ? ' active' : ''}`}
      onClick={() => commitPatch({ textAlign: a })}
      aria-label={label}
      aria-pressed={align === a}
      title={label}
    >
      <Icon size={16} />
    </button>
  );

  return (
    <aside className="ve-props-panel" aria-label={t('video_editor_props.text_props_aria')}>
      <PanelHeader icon={<Type size={16} />} title={t('video_editor_props.text_title')} subtitle={timingSubtitle} />

      <PanelSectionTitle>{t('video_editor_props.canvas_section')}</PanelSectionTitle>
      <div className="ve-props-canvas-grid ve-props-canvas-grid--2">
        <label className="ve-props-mini-label" htmlFor="ve-t-x">
          {t('video_editor_props.canvas_x_pct')}
          <input
            id="ve-t-x"
            type="number"
            className="ve-props-num"
            step={0.1}
            value={ratioToPct(selected.x)}
            onChange={e => {
              const v = Number(e.target.value);
              if (!Number.isFinite(v)) return;
              patch({ x: pctToRatio(v) });
            }}
            onBlur={() => onCommitHistory()}
          />
        </label>
        <label className="ve-props-mini-label" htmlFor="ve-t-y">
          {t('video_editor_props.canvas_y_pct')}
          <input
            id="ve-t-y"
            type="number"
            className="ve-props-num"
            step={0.1}
            value={ratioToPct(selected.y)}
            onChange={e => {
              const v = Number(e.target.value);
              if (!Number.isFinite(v)) return;
              patch({ y: pctToRatio(v) });
            }}
            onBlur={() => onCommitHistory()}
          />
        </label>
      </div>
      <p className="ve-props-hint">{t('video_editor_props.canvas_hint_text')}</p>

      <PanelSectionTitle>{t('video_editor_panel.appearance', 'Appearance')}</PanelSectionTitle>

      <label className="ve-props-label" htmlFor="ve-props-text">
        {t('video_editor_props.content')}
        <textarea
          id="ve-props-text"
          className="ve-props-textarea"
          rows={4}
          value={selected.text}
          onChange={e => patch({ text: e.target.value })}
          onBlur={() => onCommitHistory()}
          placeholder={t('video_editor_props.content_placeholder')}
        />
      </label>

      <label className="ve-props-label" htmlFor="ve-props-font">
        {t('video_editor_props.font')}
        <select
          id="ve-props-font"
          className="ve-props-select"
          value={selected.fontFamily ?? 'Serif'}
          onChange={e => {
            loadGoogleFontIfNeeded(e.target.value);
            commitPatch({ fontFamily: e.target.value });
          }}
        >
          {VIDEO_EDITOR_FONT_FAMILIES.map(f => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </label>

      <div className="ve-props-row">
        <label className="ve-props-label ve-props-grow" htmlFor="ve-props-size">
          {t('video_editor_props.size')}
          <input
            id="ve-props-size"
            type="range"
            min={8}
            max={96}
            value={selected.fontSize}
            onChange={e => patch({ fontSize: Number(e.target.value) })}
            onMouseUp={onCommitHistory}
            onKeyUp={e => e.key === 'Enter' && onCommitHistory()}
          />
        </label>
        <span className="ve-props-size-value">{selected.fontSize}px</span>
      </div>

      <div className="ve-props-row ve-props-colors">
        <label className="ve-props-label">
          {t('video_editor_props.text_color')}
          <input
            type="color"
            className="ve-props-color"
            value={toHexColor(selected.color)}
            onChange={e => patch({ color: e.target.value })}
            onBlur={onCommitHistory}
            aria-label={t('video_editor_props.text_color')}
          />
        </label>
        <label className="ve-props-label">
          {t('video_editor_props.background')}
          <input
            type="color"
            className="ve-props-color"
            value={toHexColor(selected.backgroundColor === 'transparent' ? '#000000' : (selected.backgroundColor || '#000000'))}
            onChange={e => patch({ backgroundColor: e.target.value })}
            onBlur={onCommitHistory}
            aria-label={t('video_editor_props.background')}
          />
        </label>
        <button
          type="button"
          className="ve-props-btn-secondary"
          onClick={() => commitPatch({ backgroundColor: 'transparent' })}
        >
          {t('video_editor_props.clear_bg')}
        </button>
      </div>

      <div className="ve-props-label">
        {t('video_editor_props.style')}
        <div className="ve-props-toggle-row" role="group" aria-label={t('video_editor_props.style')}>
          <button
            type="button"
            className={`ve-props-toggle${selected.bold ? ' active' : ''}`}
            onClick={() => commitPatch({ bold: !selected.bold })}
            aria-pressed={!!selected.bold}
            aria-label={t('video_editor_props.bold')}
            title={t('video_editor_props.bold')}
          >
            <Bold size={16} />
          </button>
          <button
            type="button"
            className={`ve-props-toggle${selected.italic ? ' active' : ''}`}
            onClick={() => commitPatch({ italic: !selected.italic })}
            aria-pressed={!!selected.italic}
            aria-label={t('video_editor_props.italic')}
            title={t('video_editor_props.italic')}
          >
            <Italic size={16} />
          </button>
          <button
            type="button"
            className={`ve-props-toggle${selected.underline ? ' active' : ''}`}
            onClick={() => commitPatch({ underline: !selected.underline })}
            aria-pressed={!!selected.underline}
            aria-label={t('video_editor_props.underline')}
            title={t('video_editor_props.underline')}
          >
            <Underline size={16} />
          </button>
        </div>
      </div>

      <div className="ve-props-label">
        {t('video_editor_props.align')}
        <div className="ve-props-toggle-row" role="group" aria-label={t('video_editor_props.align')}>
          {alignBtn('left', AlignLeft, t('video_editor_props.align_left'))}
          {alignBtn('center', AlignCenter, t('video_editor_props.align_center'))}
          {alignBtn('right', AlignRight, t('video_editor_props.align_right'))}
          {alignBtn('justify', AlignJustify, t('video_editor_props.align_justify'))}
        </div>
      </div>

      <label className="ve-props-label" htmlFor="ve-props-boxw">
        {t('video_editor_props.box_width')}
        <input
          id="ve-props-boxw"
          type="range"
          min={15}
          max={90}
          value={Math.round((selected.textBoxWidthRatio ?? 0.45) * 100)}
          onChange={e =>
            patch({ textBoxWidthRatio: Number(e.target.value) / 100 })
          }
          onMouseUp={onCommitHistory}
        />
      </label>
      <span className="ve-props-hint">
        {t('video_editor_props.box_width_hint', { pct: Math.round((selected.textBoxWidthRatio ?? 0.45) * 100) })}
      </span>

      {actionBar}
    </aside>
  );
}

function toHexColor(c: string): string {
  if (!c || c === 'transparent') return '#000000';
  if (/^#[0-9a-fA-F]{6}$/.test(c)) return c;
  const m = c.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (m) {
    const r = Number(m[1]).toString(16).padStart(2, '0');
    const g = Number(m[2]).toString(16).padStart(2, '0');
    const b = Number(m[3]).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }
  return '#ffffff';
}
